import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, CheckCircle2, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global notification bell — visible on every page.
 * Combines task-assignment notifications + Slate @mentions in one panel.
 * Polls every 20s.
 */
export default function NotificationBell({ projectId = null }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [unread, setUnread]     = useState(0);
  const [open, setOpen]         = useState(false);
  const panelRef                = useRef(null);

  const fetchItems = useCallback(async () => {
    if (!token) return;
    try {
      const url = projectId
        ? `${API}/notifications/unified?project_id=${projectId}`
        : `${API}/notifications/unified`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch (e) { /* silent — auth/network errors are OK to ignore here */ }
  }, [token, projectId]);

  useEffect(() => {
    fetchItems();
    const id = setInterval(fetchItems, 20000);
    return () => clearInterval(id);
  }, [fetchItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      const url = projectId
        ? `${API}/notifications/unified/mark-all-read?project_id=${projectId}`
        : `${API}/notifications/unified/mark-all-read`;
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchItems();
    } catch { /* noop */ }
  };

  const onClickItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  if (!token) return null;

  return (
    <div ref={panelRef} className="absolute top-3 right-4 z-40" data-testid="notification-bell-wrapper">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative h-9 w-9 rounded-full bg-[#13151C] border border-[#1E2028] flex items-center justify-center text-[#A0A4B8] hover:text-[#F5A623] hover:border-[#F5A623]/40 transition-colors shadow-md"
        data-testid="notification-bell-btn"
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-semibold"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-lg bg-[#13151C] border border-[#1E2028] shadow-2xl"
          data-testid="notification-bell-panel"
        >
          <div className="sticky top-0 bg-[#13151C] border-b border-[#1E2028] px-3 py-2 flex items-center justify-between z-10">
            <span className="text-sm font-semibold text-[#FFFFFF]">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-[10px] text-[#4a9eff] hover:underline"
                data-testid="notification-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#A0A4B8] text-sm" data-testid="notification-empty">
              You're all caught up.
            </div>
          ) : (
            <div className="divide-y divide-[#1E2028]">
              {items.map(n => {
                const Icon = n.kind === 'mention' ? MessageSquare : CheckCircle2;
                const iconColor = n.kind === 'mention' ? '#F5A623' : '#4a9eff';
                return (
                  <button
                    key={`${n.kind}-${n.id}`}
                    onClick={() => onClickItem(n)}
                    className={`w-full text-left px-3 py-2.5 flex items-start gap-2 transition-colors ${
                      !n.read ? 'bg-[#0f0f10]' : ''
                    } hover:bg-[#222]`}
                    data-testid={`notification-item-${n.kind}-${n.id}`}
                  >
                    <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: iconColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!n.read && <span className="h-1.5 w-1.5 bg-[#F5A623] rounded-full flex-shrink-0" />}
                        <span className="text-xs text-[#FFFFFF] leading-snug">{n.message}</span>
                      </div>
                      <span className="text-[10px] text-[#666] mt-0.5 block">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
